import { isDevEnvironment } from '../../../utilities/env';
import { DEV_FLOW_NAME_TO_ROUTES, FLOW_NAMES } from './constants';

export const splitFlowUrl = (fullPathUrl, flowSteps) => {
  if (!flowSteps?.screens || !flowSteps?.basePath) {
    return { currentScreenIndex: -1 };
  }

  const pathData = validatePathAndExtractParams(fullPathUrl, flowSteps.basePath);

  if (!pathData.isValidPath) return { currentScreenIndex: -1 };

  const currentScreenIndex = findMatchingScreenIndex(flowSteps.screens, pathData.slugUrlSegments);

  if (currentScreenIndex >= 0) {
    const currentScreenUrlSlug = flowSteps.screens[currentScreenIndex].screenUrlSlug;
    const basePath = currentScreenUrlSlug === '/' ? fullPathUrl : fullPathUrl.slice(0, -currentScreenUrlSlug.length);

    return {
      basePath,
      currentScreenIndex,
      params: Object.keys(pathData.params).length ? pathData.params : {}
    };
  }

  return { currentScreenIndex: -1 };
};

/**
 * Validates whether the given full path URL matches the specified base path,
 * and extracts any route parameters defined in the base path (e.g., ":id").
 *
 * @returns {isValidPath: false} | {Object} An object containing:
 *   - {boolean} isValidPath - Indicates whether the full path matches the base path.
 *   - {Object} params - An object mapping parameter names to their values (preserving original case).
 *   - {string[]} fullPathSegments - Array of segments from fullPath (e.g: '/foo/bar/baz/' → ['foo', 'bar', 'baz'])
 *   - {string[]} basePathSegments - Array of segments from basePath
 *   - {string[]} slugUrlSegments - Array of segments representing slug URL (i.e., the part of the path after the base path).
 */

const validatePathAndExtractParams = (fullPath, basePath) => {
  const fullPathSegments = fullPath.split('/').filter(Boolean); // .filter(Boolean) removes empty segments
  const basePathSegments = basePath.split('/').filter(Boolean);
  const params = {};

  // Early exit if path is shorter than base path
  if (fullPathSegments.length < basePathSegments.length) {
    return { isValidPath: false };
  }

  for (let i = 0; i < basePathSegments.length; i++) {
    const basePathSegment = basePathSegments[i];
    const fullPathSegment = fullPathSegments[i];

    if (basePathSegment.startsWith(':')) {
      const paramName = basePathSegment.slice(1);
      params[paramName] = fullPathSegment;
    } else if (basePathSegment.toLowerCase() !== fullPathSegment.toLowerCase()) {
      return { isValidPath: false };
    }
  }

  return {
    isValidPath: true,
    params,
    fullPathSegments,
    basePathSegments,
    slugUrlSegments: fullPathSegments.slice(basePathSegments.length)
  };
};

/**
 * Finds index of matching screen in flow configuration
 */
const findMatchingScreenIndex = (screens, slugUrlSegments) => {
  return screens.findIndex(step => {
    // handle edge case where screenUrlSlug is '/' and slugUrlSegments is empty
    if (step.screenUrlSlug === '/' && slugUrlSegments.length === 0) {
      return true;
    }
    const slugUrl = '/' + slugUrlSegments.join('/');
    return slugUrl.toLowerCase() === step.screenUrlSlug.toLowerCase();
  });
};

/**
 * Hides the cookie notification element and returns a cleanup function
 * to restore the display styles of the header and cookie notification.
 */
export const handleCookieNotification = () => {
  const cookieNotificationElement = document.getElementsByClassName('cookie-notification')[0];

  if (cookieNotificationElement) {
    cookieNotificationElement.style.display = 'none';
  }

  return () => {
    const headerContainer = document.getElementsByClassName('header-container')[0];
    const cookieNotification = document.getElementsByClassName('cookie-notification')[0];

    if (headerContainer) {
      headerContainer.style.display = 'block';
    }

    if (cookieNotification) {
      cookieNotification.style.display = 'block';
    }
  };
};

/**
 * Gets the appropriate rule set based on offer code exclusions
 * @returns {Object} The matched rule set
 */
export const getRuleSet = (displayRuleSet, offerCode) => {
  return displayRuleSet?.find(rs => {
    if (!rs.condition) return true;
    if (!rs.condition.promoExcludes) return true;
    return !rs.condition.promoExcludes.includes(offerCode);
  });
};

/**
 * Selects a modal configuration from the rule set based on display weights
 * @param {Object} ruleSet - The rule set to use for selection
 * @returns {Object} The selected modal configuration
 */
export const determineWhichFlow = flowName => {
  if (process.env.NODE_ENV !== 'production' && !Object.values(FLOW_NAMES).includes(flowName)) {
    console.error(
      new Error(
        `determineWhichFlow: invalid flowName "${flowName}". Expected one of: ${Object.values(FLOW_NAMES).join(', ')}`
      )
    );
  }
  const whichFlow = {
    isSuiteBookingFlow: flowName === FLOW_NAMES.BOOKING,
    isPartyBookingFlow: flowName === FLOW_NAMES.BIRTHDAY,
    isModifyFlow: flowName === FLOW_NAMES.MODIFY
  };
  whichFlow.isDaypassBookingFlow =
    flowName === FLOW_NAMES.DAYPASS ||
    !(whichFlow.isSuiteBookingFlow || whichFlow.isModifyFlow || whichFlow.isPartyBookingFlow);
  return whichFlow;
};

/**
 * LOCAL ONLY: Returns the flow name (e.g., 'booking', 'birthday', 'modify') based on the given pathname.
 *
 * This is used only in development to infer the current flow by matching the pathname
 * against known route patterns defined in DEV_FLOW_NAME_TO_ROUTES.
 *
 * In production, this logic is handled via the AEM HTML markup (e.g., data-flow attribute),
 * so this function will return `null` and log a warning when called outside a dev environment.
 */
export const getDevFlowNameFromPath = pathname => {
  if (!isDevEnvironment) {
    console.warn('getDevFlowNameFromPath called outside development. Returning null.');
    return null;
  }

  for (const [flowName, routes] of Object.entries(DEV_FLOW_NAME_TO_ROUTES)) {
    for (const route of routes) {
      const result = validatePathAndExtractParams(pathname, route);
      if (result.isValidPath && result.slugUrlSegments.length === 0) {
        return flowName;
      }
    }
  }

  return null;
};
